import { useMemo } from 'react';

import { Columns } from '@/components/table/types';
import { useSmartColumnPositioning } from '@/hooks/useSmartColumnPositioning';
import {
  getColumns,
  getSortableColumnsConfig,
} from '@/sections/asset/config/columnDefinitions';
import { Asset } from '@/types';

/**
 * Return type for useAssetColumns hook
 */
export interface UseAssetColumnsReturn {
  /** Rendered table columns with current configuration */
  columns: Columns<Asset>;
  /** Currently active column IDs in display order */
  activeColumns: string[] | undefined;
  /** Function to update active columns with user ordering */
  setActiveColumns: (newColumns: string[]) => void;
  /** Placeholder text showing column count (e.g., "Columns (8/12)") */
  placeholder: string;
  /** Column configuration for sortable column component */
  sortableColumnsConfig: ReturnType<typeof getSortableColumnsConfig>;
}

/**
 * Custom hook for managing asset table columns with smart positioning.
 *
 * Uses the shared useSmartColumnPositioning hook to ensure status column
 * returns to first position and date columns to last position when toggled on,
 * while preserving user drag-to-reorder preferences.
 *
 * Handles:
 * - Column definitions and default configuration
 * - Smart positioning: status first, origin 3rd-to-last, tags before dates, dates last, others after identifier
 * - Active vs available column state
 * - Column count placeholder generation
 * - User column ordering preferences
 *
 * Positioning Rules:
 * - Status column → first position when toggled on
 * - Origin column → 3rd-to-last position (before tags and dates) when toggled on
 * - Tags column → before date columns when toggled on
 * - Date columns (created, visited) → last positions when toggled on
 * - Other columns → after identifier when toggled on
 * - User drag-to-reorder preferences are preserved
 *
 * @returns Column configuration state and controls
 *
 * @example
 * ```tsx
 * const { columns, activeColumns, setActiveColumns, placeholder, sortableColumnsConfig } =
 *   useAssetColumns();
 *
 * <Table
 *   columns={columns}
 *   sortableColumns={{
 *     activeColumns,
 *     setActiveColumns,
 *     defaultConfig: sortableColumnsConfig,
 *     placeholder,
 *   }}
 * />
 * ```
 */
export function useAssetColumns(): UseAssetColumnsReturn {
  // Initialize default column definitions
  const defaultColumns: Columns<Asset> = useMemo(
    () => getColumns() as Columns<Asset>,
    []
  );

  // Generate sortable columns configuration
  const sortableColumnsConfig = useMemo(
    () => getSortableColumnsConfig({ defaultColumns }),
    [defaultColumns]
  );

  // Initialize smart column positioning with positioning rules
  const { activeColumns, setActiveColumns, columns } =
    useSmartColumnPositioning({
      key: 'assets.v5', // v5: Simple default ordering, user has full control
      defaultColumns,
      defaultConfig: sortableColumnsConfig,
      positioningRules: {
        firstColumns: ['status'], // Status → first when toggled on
        beforeLastColumns: ['origins'], // Origin (before dates) when toggled on
        lastColumns: ['created', 'visited'], // Date columns → last when toggled on
        afterColumn: {
          // Non-special columns insert after identifier
          ids: ['asname', 'asnumber', 'country', 'ipVersion', 'attackSurface'],
          after: 'identifier', // Insert after identifier column
        },
      },
    });

  // Generate enhanced placeholder showing active/total column counts
  const placeholder = useMemo(() => {
    const total =
      sortableColumnsConfig.active.length +
      sortableColumnsConfig.available.length;
    const active = activeColumns?.length ?? 0;
    return `Columns (${active}/${total})`;
  }, [
    activeColumns?.length,
    sortableColumnsConfig.active.length,
    sortableColumnsConfig.available.length,
  ]);

  return {
    columns,
    activeColumns,
    setActiveColumns,
    placeholder,
    sortableColumnsConfig,
  };
}
